import React, { useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import Footer from "@/components/Footer";

const DeleteAccount = () => {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!confirmed) {
      setStatus({
        type: "error",
        message: "Veuillez confirmer que vous comprenez que cette action est irréversible."
      });
      return;
    }

    // Créer un email de demande de suppression
    const subject = encodeURIComponent("Demande de suppression de compte NutriNori");
    const body = encodeURIComponent(
      `Bonjour,\n\nJe souhaite supprimer mon compte NutriNori.\n\nEmail du compte : ${email}\n\nRaison (optionnelle) : ${reason || 'Non spécifiée'}\n\nJe comprends que cette action est irréversible et que toutes mes données seront définitivement supprimées.\n\nCordialement`
    );

    window.location.href = `mailto:support@nutrinori.com?subject=${subject}&body=${body}`;

    setStatus({
      type: "success",
      message: "Votre client email va s'ouvrir pour envoyer la demande."
    });
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#0A0A0A" }}>
      <div className="flex-1">
        <div className="border-b" style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}>
          <div className="max-w-4xl mx-auto px-6 py-4">
            <a href="/" className="text-xl font-semibold" style={{ color: "#3D6B4A" }}>
              NutriNori
            </a>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-6 py-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-full" style={{ backgroundColor: "rgba(239, 68, 68, 0.1)" }}>
              <Trash2 className="w-6 h-6" style={{ color: "#EF4444" }} />
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#FFFFFF" }}>
              Supprimer mon compte
            </h1>
          </div>

          <div 
            className="p-4 rounded-lg mb-8 flex gap-3"
            style={{ 
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.3)"
            }}
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#EF4444" }} />
            <div>
              <p className="font-semibold mb-2" style={{ color: "#EF4444" }}>
                Attention : Cette action est irréversible
              </p>
              <p className="text-sm" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                La suppression de votre compte entraînera la perte définitive de :
              </p>
              <ul className="text-sm mt-2 space-y-1 list-disc pl-5" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                <li>Votre profil et vos informations personnelles</li>
                <li>L'historique complet de vos repas et analyses</li>
                <li>Vos préférences et paramètres</li>
                <li>Votre abonnement NutriNori Pro (si applicable)</li>
              </ul>
            </div>
          </div>

          <div 
            className="p-6 rounded-lg mb-8"
            style={{ 
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)"
            }}
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: "#FFFFFF" }}>
              Alternatives à la suppression
            </h2>
            <div className="space-y-3 text-sm" style={{ color: "rgba(255, 255, 255, 0.7)" }}>
              <p>
                <strong style={{ color: "#FFFFFF" }}>Vous souhaitez faire une pause ?</strong><br />
                Vous pouvez simplement vous déconnecter de l'application. Vos données seront conservées et vous pourrez revenir à tout moment.
              </p>
              <p>
                <strong style={{ color: "#FFFFFF" }}>Problème avec l'application ?</strong><br />
                Contactez notre support à{" "}
                <a href="mailto:support@nutrinori.com" style={{ color: "#3D6B4A" }} className="hover:underline">
                  support@nutrinori.com
                </a>
                . Nous serons ravis de vous aider.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label 
                htmlFor="email"
                className="block text-sm font-medium mb-2"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                Email de votre compte NutriNori
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="votre@email.com"
                className="w-full px-4 py-3 rounded-lg transition-all duration-200"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#FFFFFF",
                  outline: "none"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#EF4444";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                }}
              />
            </div>

            <div>
              <label 
                htmlFor="reason"
                className="block text-sm font-medium mb-2"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                Raison de la suppression (optionnel)
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows="4"
                placeholder="Aidez-nous à améliorer NutriNori en nous expliquant pourquoi vous partez..."
                className="w-full px-4 py-3 rounded-lg transition-all duration-200 resize-none"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  color: "#FFFFFF",
                  outline: "none"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#EF4444";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.08)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.05)";
                }}
              />
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="confirm"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1"
                style={{ accentColor: "#EF4444" }}
              />
              <label 
                htmlFor="confirm"
                className="text-sm cursor-pointer"
                style={{ color: "rgba(255, 255, 255, 0.9)" }}
              >
                Je comprends que cette action est irréversible et que toutes mes données seront définitivement supprimées dans un délai de 30 jours.
              </label>
            </div>

            {status && (
              <div
                className="px-4 py-3 rounded-lg text-sm"
                style={{
                  backgroundColor: status.type === "success" 
                    ? "rgba(61, 107, 74, 0.15)" 
                    : "rgba(239, 68, 68, 0.15)",
                  color: status.type === "success" ? "#3D6B4A" : "#EF4444",
                  border: `1px solid ${status.type === "success" ? "rgba(61, 107, 74, 0.3)" : "rgba(239, 68, 68, 0.3)"}`
                }}
                role="alert"
              >
                {status.message}
              </div>
            )}

            <div className="flex gap-4">
              <a
                href="/support"
                className="flex-1 px-6 py-4 rounded-lg font-medium text-center transition-all duration-200"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  color: "#FFFFFF",
                  border: "1px solid rgba(255, 255, 255, 0.1)"
                }}
              >
                Annuler
              </a>
              <button
                type="submit"
                disabled={!confirmed}
                className="flex-1 px-6 py-4 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: "#EF4444",
                  color: "#FFFFFF"
                }}
                onMouseEnter={(e) => {
                  if (confirmed) e.currentTarget.style.backgroundColor = "#DC2626";
                }}
                onMouseLeave={(e) => {
                  if (confirmed) e.currentTarget.style.backgroundColor = "#EF4444";
                }}
              >
                Supprimer mon compte
              </button>
            </div>
          </form>

          <div className="mt-8 text-center text-sm" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
            <p>
              Vous pouvez également faire votre demande directement par email à{" "}
              <a href="mailto:support@nutrinori.com" style={{ color: "#3D6B4A" }} className="hover:underline">
                support@nutrinori.com
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer theme="dark" />
    </div>
  );
};

export default DeleteAccount;
